/**
 * AnimatedCursor
 * ──────────────
 * Custom cursor with contextual states.
 * Water droplet effect with text labels.
 */

import { useEffect, useRef, useState, createContext, useContext } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion, isMobile } from './tokens';

// Cursor context for state management
const CursorContext = createContext({
  setCursorState: () => {},
  setCursorText: () => {},
});

export const useCursor = () => useContext(CursorContext);

// Cursor states configuration
const CURSOR_STATES = {
  default: { scale: 1, opacity: 0.7, mixBlendMode: 'difference' },
  hover: { scale: 1.8, opacity: 1, mixBlendMode: 'difference' },
  image: { scale: 2.5, opacity: 1, mixBlendMode: 'normal', bg: 'rgba(0,200,220,0.2)' },
  link: { scale: 1.4, opacity: 1, mixBlendMode: 'difference' },
  play: { scale: 3, opacity: 1, mixBlendMode: 'normal', bg: 'rgba(0,200,220,0.15)' },
  drag: { scale: 2, opacity: 1, mixBlendMode: 'difference' },
  hidden: { scale: 0, opacity: 0 },
};

export function CursorProvider({ children }) {
  const [cursorState, setCursorState] = useState('default');
  const [cursorText, setCursorText] = useState('');
  
  return (
    <CursorContext.Provider value={{ cursorState, setCursorState, cursorText, setCursorText }}>
      {children}
      <AnimatedCursor state={cursorState} text={cursorText} />
    </CursorContext.Provider>
  );
}

function AnimatedCursor({ state = 'default', text = '' }) {
  const cursorRef = useRef(null);
  const dotRef = useRef(null);
  const textRef = useRef(null);
  const positionRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const [isEnabled, setIsEnabled] = useState(false);
  
  // Check if cursor should be enabled
  useEffect(() => {
    setIsEnabled(!isMobile() && !prefersReducedMotion());
  }, []);
  
  // Mouse tracking and animation
  useEffect(() => {
    if (!isEnabled) return;
    
    const cursor = cursorRef.current;
    const dot = dotRef.current;
    if (!cursor || !dot) return;
    
    // Mouse move handler
    const handleMouseMove = (e) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
    };
    
    // Animation loop with lag
    let rafId;
    const tick = () => {
      const pos = positionRef.current;
      const target = targetRef.current;
      
      // Smooth follow (80ms lag effect)
      pos.x += (target.x - pos.x) * 0.15;
      pos.y += (target.y - pos.y) * 0.15;
      
      gsap.set(cursor, {
        x: pos.x,
        y: pos.y,
      });
      
      // Dot follows faster
      gsap.set(dot, {
        x: target.x,
        y: target.y,
      });
      
      rafId = requestAnimationFrame(tick);
    };
    
    // Hide default cursor
    document.body.style.cursor = 'none';
    
    // Add link detection
    const handleMouseOver = (e) => {
      const target = e.target;
      if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.closest('a') || target.closest('button')) {
        if (cursor) cursor.dataset.state = 'hover';
      }
    };
    
    const handleMouseOut = (e) => {
      const target = e.target;
      if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.closest('a') || target.closest('button')) {
        if (cursor) cursor.dataset.state = 'default';
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    tick();
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      cancelAnimationFrame(rafId);
      document.body.style.cursor = '';
    };
  }, [isEnabled]);
  
  // Update cursor state
  useEffect(() => {
    if (!isEnabled) return;
    
    const cursor = cursorRef.current;
    if (!cursor) return;
    
    const config = CURSOR_STATES[state] || CURSOR_STATES.default;
    
    gsap.to(cursor, {
      scale: config.scale,
      opacity: config.opacity,
      duration: 0.3,
      ease: "power2.out",
    });
    
    cursor.style.mixBlendMode = config.mixBlendMode || 'difference';
    cursor.style.backgroundColor = config.bg || 'transparent';
  }, [state, isEnabled]);
  
  // Update cursor text
  useEffect(() => {
    if (!isEnabled) return;
    
    const textEl = textRef.current;
    if (!textEl) return;
    
    if (text) {
      gsap.to(textEl, {
        opacity: 1,
        scale: 1,
        duration: 0.2,
      });
    } else {
      gsap.to(textEl, {
        opacity: 0,
        scale: 0.8,
        duration: 0.15,
      });
    }
  }, [text, isEnabled]);
  
  // Don't render on mobile or reduced motion
  if (!isEnabled) {
    return null;
  }
  
  return (
    <>
      {/* Main cursor ring */}
      <div
        ref={cursorRef}
        data-state={state}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: '1px solid rgba(240,244,248,0.5)',
          pointerEvents: 'none',
          zIndex: 99999,
          transform: 'translate(-50%, -50%)',
          willChange: 'transform',
          mixBlendMode: 'difference',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Text label */}
        <span
          ref={textRef}
          style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '9px',
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            opacity: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </span>
      </div>
      
      {/* Center dot */}
      <div
        ref={dotRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: '#00C8DC',
          pointerEvents: 'none',
          zIndex: 99999,
          transform: 'translate(-50%, -50%)',
          willChange: 'transform',
        }}
      />
    </>
  );
}

export default AnimatedCursor;
